import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { Plus, Minus, Layers, X, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { ServiceRequest, ServiceDefinition, User, Department } from '../types';
import { MapLayer } from '../services/api';
import { useTranslation } from '../context/TranslationContext';
import 'leaflet/dist/leaflet.css';

interface StaffDashboardMapProps {
    apiKey?: string;
    mapId?: string | null;
    requests: ServiceRequest[];
    services: ServiceDefinition[];
    departments: Department[];
    users: User[];
    mapLayers: MapLayer[];
    townshipBoundary?: object | null;
    defaultCenter?: { lat: number; lng: number };
    defaultZoom?: number;
    onRequestSelect: (requestId: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
    open: '#ef4444',
    in_progress: '#f59e0b',
    closed: '#22c55e',
};
const STATUS_LABELS: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    closed: 'Closed',
};

function makeStatusIcon(color: string, scale: number = 1) {
    const s = Math.round(20 * scale);
    return L.divIcon({
        html: `<div style="width:${s}px;height:${s}px;background:${color};border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.35)"></div>`,
        className: '',
        iconSize: [s, s],
        iconAnchor: [s / 2, s / 2],
        popupAnchor: [0, -s / 2],
    });
}

function FitBounds({ bounds }: { bounds: L.LatLngBounds | null }) {
    const map = useMap();
    const fittedRef = useRef(false);
    useEffect(() => {
        if (bounds && bounds.isValid() && !fittedRef.current) {
            fittedRef.current = true;
            map.fitBounds(bounds, { padding: [40, 40] });
        }
    }, [bounds, map]);
    return null;
}

function InvalidateSize() {
    const map = useMap();
    useEffect(() => {
        // Staggered invalidations cover animated containers (framer-motion, etc.)
        const t1 = setTimeout(() => map.invalidateSize(), 50);
        const t2 = setTimeout(() => map.invalidateSize(), 300);
        const t3 = setTimeout(() => map.invalidateSize(), 800);
        // orientationchange is safe — it's a one-shot event, not continuous like resize
        const onOrientation = () => setTimeout(() => map.invalidateSize(), 300);
        window.addEventListener('orientationchange', onOrientation);
        return () => {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
            window.removeEventListener('orientationchange', onOrientation);
        };
    }, [map]);
    return null;
}

function CustomZoom() {
    const map = useMap();
    return (
        <div className="absolute bottom-20 left-3 z-[1000] flex flex-col gap-1">
            <button
                onClick={() => map.zoomIn()}
                className="w-9 h-9 bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 shadow-lg flex items-center justify-center hover:bg-white transition-all active:scale-95"
                aria-label="Zoom in"
            >
                <Plus className="w-4 h-4 text-gray-700" />
            </button>
            <button
                onClick={() => map.zoomOut()}
                className="w-9 h-9 bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 shadow-lg flex items-center justify-center hover:bg-white transition-all active:scale-95"
                aria-label="Zoom out"
            >
                <Minus className="w-4 h-4 text-gray-700" />
            </button>
        </div>
    );
}

export default function StaffDashboardMap({
    requests,
    departments,
    users,
    mapLayers,
    townshipBoundary,
    defaultCenter = { lat: 4.189, lng: 73.513 },
    defaultZoom = 13,
    onRequestSelect,
}: StaffDashboardMapProps) {
    const { language: _language } = useTranslation();

    const [statusFilters, setStatusFilters] = useState({ open: true, in_progress: true, closed: true });
    const [categoryFilters, setCategoryFilters] = useState<Record<string, boolean>>({});
    const [departmentFilters, setDepartmentFilters] = useState<Record<string, boolean>>({});
    const [staffFilters, setStaffFilters] = useState<Record<string, boolean>>({});
    const [priorityFilters, setPriorityFilters] = useState({ high: true, medium: true, low: true });
    const [assignmentFilter, setAssignmentFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [expandedStatus, setExpandedStatus] = useState(true);
    const [expandedCategories, setExpandedCategories] = useState(false);
    const [expandedDepartments, setExpandedDepartments] = useState(false);
    const [expandedStaff, setExpandedStaff] = useState(false);
    const [boundaryBounds, setBoundaryBounds] = useState<L.LatLngBounds | null>(null);

    const uniqueCategories = Array.from(new Set(requests.map(r => r.service_code))).map(code => {
        const req = requests.find(r => r.service_code === code);
        return { code, name: req?.service_name || code };
    });
    const uniqueDepartments = Array.from(new Set(requests.map(r => (r as any).assigned_department_name || 'Unassigned'))).sort();
    const uniqueStaff = Array.from(new Set(requests.map(r => (r as any).assigned_to || 'Unassigned'))).sort();

    useEffect(() => {
        const f: Record<string, boolean> = {};
        uniqueCategories.forEach(c => { f[c.code] = categoryFilters[c.code] ?? true; });
        setCategoryFilters(f);
    }, [requests.map(r => r.service_code).join(',')]);

    useEffect(() => {
        const f: Record<string, boolean> = {};
        uniqueDepartments.forEach(d => { f[d] = departmentFilters[d] ?? true; });
        setDepartmentFilters(f);
    }, [requests.map(r => (r as any).assigned_department_name || '').join(',')]);

    useEffect(() => {
        const f: Record<string, boolean> = {};
        uniqueStaff.forEach(s => { f[s] = staffFilters[s] ?? true; });
        setStaffFilters(f);
    }, [requests.map(r => (r as any).assigned_to || '').join(',')]);

    useEffect(() => {
        if (!townshipBoundary) return;
        try {
            const gj = townshipBoundary as any;
            const bounds = L.latLngBounds([]);
            const addCoords = (coords: number[][]) => coords.forEach(([lng, lat]) => bounds.extend([lat, lng]));
            const extractGeom = (geom: any) => {
                if (!geom) return;
                if (geom.type === 'Polygon') geom.coordinates.forEach((r: number[][]) => addCoords(r));
                else if (geom.type === 'MultiPolygon') geom.coordinates.forEach((p: number[][][]) => p.forEach(r => addCoords(r)));
            };
            if (gj.type === 'FeatureCollection') gj.features?.forEach((f: any) => extractGeom(f.geometry));
            else if (gj.type === 'Feature') extractGeom(gj.geometry);
            else extractGeom(gj);
            if (bounds.isValid()) setBoundaryBounds(bounds);
        } catch { /* ignore */ }
    }, []);

    const filteredRequests = requests.filter(r => {
        if (!statusFilters[r.status as keyof typeof statusFilters]) return false;
        if (categoryFilters[r.service_code] === false) return false;
        const dept = (r as any).assigned_department_name || 'Unassigned';
        if (departmentFilters[dept] === false) return false;
        const staff = (r as any).assigned_to || 'Unassigned';
        if (staffFilters[staff] === false) return false;
        if (assignmentFilter) {
            const s = assignmentFilter.toLowerCase();
            if (![staff, r.service_name, r.description, r.address || ''].some(x => x.toLowerCase().includes(s))) return false;
        }
        if (!r.lat || !r.long) return false;
        const ai = (r as any).ai_analysis;
        const priority = (r as any).manual_priority_score ?? ai?.priority_score ?? 5;
        const level = priority >= 8 ? 'high' : priority >= 5 ? 'medium' : 'low';
        if (!priorityFilters[level]) return false;
        return true;
    });

    const statusCounts = {
        open: requests.filter(r => r.status === 'open').length,
        in_progress: requests.filter(r => r.status === 'in_progress').length,
        closed: requests.filter(r => r.status === 'closed').length,
    };

    const boundaryStyle: L.PathOptions = { color: '#818cf8', weight: 2, fillColor: '#6366f1', fillOpacity: 0.06, dashArray: '5, 5' };

    return (
        <div className="h-full min-h-[400px] flex relative rounded-2xl overflow-hidden border border-white/10 shadow-xl" style={{ isolation: 'isolate' }}>
            <MapContainer
                center={[defaultCenter.lat, defaultCenter.lng]}
                zoom={defaultZoom}
                className="flex-1 h-full w-full"
                style={{ background: '#1a1a2e' }}
                zoomControl={false}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <InvalidateSize />
                <FitBounds bounds={boundaryBounds} />
                <CustomZoom />

                {townshipBoundary && (
                    <GeoJSON key="boundary" data={townshipBoundary as any} style={() => boundaryStyle} />
                )}

                {/* GeoJSON map layers */}
                {mapLayers.map((layer, i) => {
                    if (!layer.geojson) return null;
                    return (
                        <GeoJSON key={`layer-${i}`} data={layer.geojson as any}
                            style={() => ({ color: layer.fill_color || '#6366f1', weight: 2, fillOpacity: 0.3 })} />
                    );
                })}

                <MarkerClusterGroup chunkedLoading>
                    {filteredRequests.map(r => (
                        <Marker
                            key={r.service_request_id}
                            position={[r.lat!, r.long!]}
                            icon={makeStatusIcon(STATUS_COLORS[r.status] || '#6366f1')}
                        >
                            <Popup>
                                <div className="min-w-[200px] p-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                            style={{ background: STATUS_COLORS[r.status] || '#6366f1' }} />
                                        <span className="font-semibold text-sm text-gray-800">{r.service_name}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-1">{r.address || 'No address'}</p>
                                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">{r.description}</p>
                                    <button
                                        className="w-full text-xs bg-indigo-600 text-white rounded px-3 py-1.5 hover:bg-indigo-700 transition-colors"
                                        onClick={() => onRequestSelect(r.service_request_id)}
                                    >
                                        View Full Details
                                    </button>
                                </div>
                            </Popup>
                        </Marker>
                    ))}
                </MarkerClusterGroup>
            </MapContainer>

            {/* Filter toggle */}
            <button
                onClick={() => setShowFilters(!showFilters)}
                className="absolute bottom-4 right-3 z-[1000] px-3 py-2 bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 hover:bg-white transition-all shadow-lg flex items-center gap-2"
            >
                <Layers className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Filters</span>
                {!Object.values(statusFilters).every(v => v) && (
                    <span className="w-2 h-2 rounded-full bg-indigo-500" />
                )}
            </button>

            {/* Filter panel */}
            {showFilters && (
                <div className="absolute bottom-14 right-3 z-[1000] w-64 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden max-h-[60vh] overflow-y-auto">
                    <div className="p-3 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
                        <h3 className="font-semibold text-gray-800 text-sm">Filter Requests</h3>
                        <button onClick={() => setShowFilters(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                            <X className="w-4 h-4 text-gray-400" />
                        </button>
                    </div>

                    <div className="p-3 space-y-3">
                        {/* Search */}
                        <input
                            type="text"
                            placeholder="Search by staff, type, address..."
                            value={assignmentFilter}
                            onChange={e => setAssignmentFilter(e.target.value)}
                            className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />

                        {/* Status */}
                        <div>
                            <button onClick={() => setExpandedStatus(!expandedStatus)}
                                className="w-full flex items-center justify-between py-1">
                                <span className="text-sm font-medium text-gray-700">Status</span>
                                {expandedStatus ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            </button>
                            {expandedStatus && (
                                <div className="space-y-1 pt-1">
                                    {Object.entries(statusFilters).map(([status, enabled]) => (
                                        <label key={status} className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={enabled}
                                                onChange={e => setStatusFilters(p => ({ ...p, [status]: e.target.checked }))}
                                                className="w-4 h-4 rounded border-gray-300 text-indigo-500" />
                                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[status] }} />
                                            <span className="text-sm text-gray-600 flex-1">{STATUS_LABELS[status]}</span>
                                            <span className="text-xs text-gray-400">{statusCounts[status as keyof typeof statusCounts]}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Priority */}
                        <div className="border-t border-gray-100 pt-2">
                            <span className="text-sm font-medium text-gray-700 block mb-1">Priority</span>
                            <div className="space-y-1">
                                {(['high', 'medium', 'low'] as const).map(level => (
                                    <label key={level} className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={priorityFilters[level]}
                                            onChange={e => setPriorityFilters(p => ({ ...p, [level]: e.target.checked }))}
                                            className="w-4 h-4 rounded border-gray-300 text-indigo-500" />
                                        <span className="text-sm text-gray-600 capitalize">{level}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Categories */}
                        <div className="border-t border-gray-100 pt-2">
                            <button onClick={() => setExpandedCategories(!expandedCategories)}
                                className="w-full flex items-center justify-between py-1">
                                <span className="text-sm font-medium text-gray-700">Categories</span>
                                {expandedCategories ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            </button>
                            {expandedCategories && (
                                <div className="space-y-1 pt-1 max-h-36 overflow-y-auto">
                                    {uniqueCategories.map(cat => (
                                        <label key={cat.code} className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={categoryFilters[cat.code] ?? true}
                                                onChange={e => setCategoryFilters(p => ({ ...p, [cat.code]: e.target.checked }))}
                                                className="w-4 h-4 rounded border-gray-300 text-indigo-500" />
                                            <span className="text-sm text-gray-600 truncate">{cat.name}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Departments */}
                        <div className="border-t border-gray-100 pt-2">
                            <button onClick={() => setExpandedDepartments(!expandedDepartments)}
                                className="w-full flex items-center justify-between py-1">
                                <span className="text-sm font-medium text-gray-700">Departments</span>
                                {expandedDepartments ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            </button>
                            {expandedDepartments && (
                                <div className="space-y-1 pt-1 max-h-36 overflow-y-auto">
                                    {uniqueDepartments.map(dept => (
                                        <label key={dept} className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={departmentFilters[dept] ?? true}
                                                onChange={e => setDepartmentFilters(p => ({ ...p, [dept]: e.target.checked }))}
                                                className="w-4 h-4 rounded border-gray-300 text-indigo-500" />
                                            <span className="text-sm text-gray-600 truncate">{dept}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Staff */}
                        <div className="border-t border-gray-100 pt-2">
                            <button onClick={() => setExpandedStaff(!expandedStaff)}
                                className="w-full flex items-center justify-between py-1">
                                <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                                    <Users className="w-3.5 h-3.5" /> Staff
                                </span>
                                {expandedStaff ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            </button>
                            {expandedStaff && (
                                <div className="space-y-1 pt-1 max-h-36 overflow-y-auto">
                                    {uniqueStaff.map(s => (
                                        <label key={s} className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={staffFilters[s] ?? true}
                                                onChange={e => setStaffFilters(p => ({ ...p, [s]: e.target.checked }))}
                                                className="w-4 h-4 rounded border-gray-300 text-indigo-500" />
                                            <span className="text-sm text-gray-600 truncate">{s}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Legend */}
            <div className="absolute bottom-4 left-3 z-[1000] bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 px-3 py-2 shadow-lg">
                <div className="flex items-center gap-3 text-xs flex-wrap">
                    {Object.entries(STATUS_COLORS).map(([status, color]) => (
                        <div key={status} className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                            <span className="text-gray-600">{STATUS_LABELS[status]}</span>
                        </div>
                    ))}
                    <span className="text-gray-400 pl-1 border-l border-gray-200">
                        <span className="text-indigo-600 font-semibold">{filteredRequests.length}</span> shown
                    </span>
                </div>
            </div>
        </div>
    );
}
